import {
  X,
  Building2,
  Star,
  Truck,
  Award,
  DollarSign,
  Mail,
  Phone,
  MapPin,
  Edit,
  FileText,
  BarChart3,
  ShoppingCart,
  Calendar,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Supplier } from '../../types';

interface SupplierDetailModalProps {
  readonly supplier: Supplier | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onEdit: (supplier: Supplier) => void;
  readonly onViewOrders: (supplier: Supplier) => void;
  readonly onViewPerformance: (supplier: Supplier) => void;
  readonly onCreatePO: (supplier: Supplier) => void;
}

export function SupplierDetailModal({
  supplier,
  isOpen,
  onClose,
  onEdit,
  onViewOrders,
  onViewPerformance,
  onCreatePO,
}: SupplierDetailModalProps) {
  if (!isOpen || !supplier) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-background p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-primary/10 p-3">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{supplier.name}</h2>
                {supplier.isActive ? (
                  <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    <XCircle className="h-3 w-3" /> Inactive
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{supplier.contactName}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Rating Badge */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1.5">
              <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
              <span className="text-lg font-semibold text-amber-700">{supplier.rating.toFixed(1)}</span>
            </div>
            <span className="text-sm text-muted-foreground">Overall Rating</span>
          </div>

          {/* Contact Information */}
          <div className="rounded-lg border p-4">
            <h3 className="font-medium mb-3">Contact Information</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a href={`mailto:${supplier.contactEmail}`} className="text-primary hover:underline">
                  {supplier.contactEmail}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <a href={`tel:${supplier.contactPhone}`} className="hover:underline">
                  {supplier.contactPhone}
                </a>
              </div>
              {supplier.deliveryZones.length > 0 && (
                <div className="flex items-start gap-2 text-sm sm:col-span-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span>Delivery Zones: {supplier.deliveryZones.join(', ')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Performance Metrics */}
          <div>
            <h3 className="font-medium mb-3">Performance Metrics</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg bg-green-50 p-3 text-center">
                <Truck className="mx-auto h-5 w-5 text-green-600" />
                <p className="mt-1 text-xl font-semibold text-green-700">{supplier.onTimeDeliveryRate}%</p>
                <p className="text-xs text-green-600">On-Time Delivery</p>
              </div>
              <div className="rounded-lg bg-blue-50 p-3 text-center">
                <Award className="mx-auto h-5 w-5 text-blue-600" />
                <p className="mt-1 text-xl font-semibold text-blue-700">{supplier.qualityScore}%</p>
                <p className="text-xs text-blue-600">Quality Score</p>
              </div>
              <div className="rounded-lg bg-purple-50 p-3 text-center">
                <DollarSign className="mx-auto h-5 w-5 text-purple-600" />
                <p className="mt-1 text-xl font-semibold text-purple-700">
                  ${(supplier.totalSpend / 1000).toFixed(0)}K
                </p>
                <p className="text-xs text-purple-600">Total Spend</p>
              </div>
              <div className="rounded-lg bg-orange-50 p-3 text-center">
                <FileText className="mx-auto h-5 w-5 text-orange-600" />
                <p className="mt-1 text-xl font-semibold text-orange-700">{supplier.totalOrders}</p>
                <p className="text-xs text-orange-600">Total Orders</p>
              </div>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h3 className="font-medium mb-3">Categories</h3>
            <div className="flex flex-wrap gap-2">
              {supplier.categories.map((category) => (
                <span
                  key={category}
                  className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
                >
                  {category}
                </span>
              ))}
            </div>
          </div>

          {/* Certifications */}
          {supplier.certifications.length > 0 && (
            <div>
              <h3 className="font-medium mb-3">Certifications</h3>
              <div className="flex flex-wrap gap-2">
                {supplier.certifications.map((cert) => (
                  <span
                    key={cert}
                    className="flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm text-green-700"
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Member Since */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>Member since {new Date(supplier.createdAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Actions Footer */}
        <div className="sticky bottom-0 border-t bg-background p-4">
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={() => onViewPerformance(supplier)}>
              <BarChart3 className="mr-2 h-4 w-4" />
              View Performance
            </Button>
            <Button variant="outline" onClick={() => onViewOrders(supplier)}>
              <FileText className="mr-2 h-4 w-4" />
              View Orders
            </Button>
            <Button variant="outline" onClick={() => onEdit(supplier)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button onClick={() => onCreatePO(supplier)}>
              <ShoppingCart className="mr-2 h-4 w-4" />
              Create PO
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
